import { HubTemplateRenameModel, rename } from "client/Api/Hub/Templates/hubTemplateManagement";
import {
	Dialog,
	Form,
	FormItem,
	TextInput,
	Toolbar,
	ValidationError
} from "client/Components";
import { useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useOpenDialog,
	useUnmountedCancellation
} from "client/Hooks";
import {
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	createValidationConfiguration
} from "client/Validation";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement, useState } from "react";

export type RenameTemplateDialogProps = {
	id: number;
	currentName: string;
	onClose(): void;
};

export const RenameTemplateDialog = (props: RenameTemplateDialogProps): ReactElement => {
	const {
		id,
		currentName,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<HubTemplateRenameModel>({
		id,
		newName: currentName
	});

	const formHandler = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const submitHandler = async (): Promise<void> => {
		try {
			setIsBusy(true);

			const validationResult: InputValidationResult = await formHandler.validation
				.validate(globalCancellation);

			if (validationResult.kind !== InputValidationResultKind.Valid) {
				setIsBusy(false);
				return;
			}

			await rename(formData, globalCancellation);
			setIsBusy(false);
			onClose();
		} catch(e) {
			if (isCancelledError(e)) {
				return;
			}

			setIsBusy(false);

			if (formHandler.validation.tryProcessServerError(e)) {
				return;
			}

			alertManager.showError(localize("_ChangesNotSaved"));
			console.error(e);
		}
	};

	return (
		<Dialog
			hasBusyIndicator={isBusy}
			trackingId="rename-hub-template-dialog"
		>
			<Dialog.Header
				onClose={onClose}
				title={localize("_RenameTemplate")}
			/>
			<Dialog.Body>
				<Form onSubmit={submitHandler}>
					<FormItem
						label={localize("_Name")}
						isRequired
					>
						<TextInput
							{...formHandler.for("newName").attachToTextInput()}
							autoFocus="focus"
						/>
						<ValidationError {...formHandler.for("newName").attachToValidationError()} />
					</FormItem>
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Rename")}
						onClick={submitHandler}
						trackingId="rename"
					/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
RenameTemplateDialog.displayName = "RenameTemplateDialog";

export type RenameTemplateDialogInitialData = Omit<RenameTemplateDialogProps, "onClose">;

export function useRenameTemplateDialog(): [
	element: ReactElement | null,
	open: (data: RenameTemplateDialogInitialData) => Promise<void>
] {
	return useOpenDialog<RenameTemplateDialogInitialData>(
		(props, onClose) => (
			<RenameTemplateDialog
				{...props}
				onClose={onClose}
			/>
		)
	);
}

const validationConfiguration: IValidationConfiguration<HubTemplateRenameModel> = createValidationConfiguration<HubTemplateRenameModel>();

validationConfiguration.forScalar("newName")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);